import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UserPreferencesRequest } from '@/api';
import { useInputText } from '@/hooks';
import { profileActions } from '@/redux/actions';
import { profileSelectors } from '@/redux/selectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

import { Validation } from '@/models';

import { Input, InputGroup, Button, AlertBox } from '@/components';

import styles from './WebhookForm.module.sass';

const WEBHOOK_URL_REGEX =
  /^https:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;

interface WebhookFormProps {
  onClose: () => void;
}

const WebhookForm: FC<WebhookFormProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    data: profileData,
    loading,
    loadingPreferences,
    error,
  } = useAppSelector(profileSelectors.getProfileSlice);
  const webhookUrl = useAppSelector(profileSelectors.webhookUrlSetting);

  const validations: Validation[] = useMemo(
    () => [
      {
        name: 'regex',
        value: WEBHOOK_URL_REGEX,
        message: t('translation.field_is_not_url'),
      },
    ],
    [t],
  );

  const webHookField = useInputText(webhookUrl || '', validations);

  const isBusy = loading || loadingPreferences;

  const submitHandler = () => {
    if (isBusy || !webHookField.isValid) {
      return;
    }

    const payload: UserPreferencesRequest = {
      preferences: { webhook: { url: webHookField.value } },
      url: profileData?.webhookUrl,
    };

    dispatch(profileActions.updatePreferences({ payload, callback: onClose }));
  };

  const cancelHandler = () => {
    error && dispatch(profileActions.setError(null));
    onClose();
  };

  const handleChange = (value: string) => {
    error && dispatch(profileActions.setError(null));
    webHookField.onChange(value);
  };

  return (
    <div className={styles.form}>
      <InputGroup
        label={t('translation.monitoring_webhook_field')}
        className={styles.field}
      >
        <Input
          id="webhook"
          name="webhook"
          type="text"
          value={webHookField.value as string}
          onChange={handleChange}
          onBlur={webHookField.onBlur}
          placeholder="https://example.com/webhook"
          clearable
          disabled={loading}
          isError={webHookField.isDirty && !webHookField.isValid}
        />
        {webHookField.isDirty && !webHookField.isValid && (
          <AlertBox type="danger" small>
            {{ description: webHookField.errors[0] }}
          </AlertBox>
        )}
        {error && (
          <AlertBox type="danger" small>
            {{ description: error }}
          </AlertBox>
        )}
      </InputGroup>

      <div className={styles.actions}>
        <Button
          type="primary"
          size="m"
          corner="rect"
          disabled={isBusy}
          isLoading={loadingPreferences}
          onClick={submitHandler}
        >
          {t('translation.save_changes_btn')}
        </Button>
        <Button
          type="secondary"
          size="m"
          corner="rect"
          disabled={isBusy}
          onClick={cancelHandler}
        >
          {t('translation.cancel_btn')}
        </Button>
      </div>
    </div>
  );
};

export default WebhookForm;
